import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  api,
  getToken,
  setWorkspaceId,
  type ArtifactDetail,
  type Comment,
  type CommentAnchor,
  type ShareAccessLevel,
} from '../api/client';
import CommentSidebar from '../components/CommentSidebar';
import InfoBanner from '../components/InfoBanner';
import SelectionComposer from '../components/comments/SelectionComposer';
import CommentMarkers from '../components/comments/CommentMarkers';
import CommentModeToolbar from '../components/comments/CommentModeToolbar';
import VersionBar from '../components/VersionBar';
import ArtifactActionsMenu from '../components/ArtifactActionsMenu';
import { ARTIFACT_ORIGIN } from '../env';
import { getResolvedTheme, getThemePreference, subscribeToThemePreference } from '../theme';
import { useResolvedCommentAnchors } from '../hooks/useResolvedCommentAnchors';

const SIDEBAR_WIDTH = 320;
const MIN_IFRAME_WIDTH = 640;
const GUEST_ALIAS_KEY = 'exhibit_guest_alias';
const GUEST_SESSION_KEY = 'exhibit_guest_session_id';

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable="true"]');
}

function findCommentById(comments: Comment[], commentId: string | null): Comment | null {
  if (!commentId) return null;
  for (const comment of comments) {
    if (comment.id === commentId) return comment;
    const reply = findCommentById(comment.replies ?? [], commentId);
    if (reply) return reply;
  }
  return null;
}

function getGuestIdentity() {
  let alias = localStorage.getItem(GUEST_ALIAS_KEY);
  let sessionId = localStorage.getItem(GUEST_SESSION_KEY);
  if (!alias) {
    alias = `Guest ${Math.random().toString(16).slice(2, 6).toUpperCase()}`;
    localStorage.setItem(GUEST_ALIAS_KEY, alias);
  }
  if (!sessionId) {
    sessionId = Math.random().toString(16).slice(2, 10);
    localStorage.setItem(GUEST_SESSION_KEY, sessionId);
  }
  return { alias, sessionId };
}

export default function SharedArtifactViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [artifact, setArtifact] = useState<ArtifactDetail | null>(null);
  const [effectiveAccess, setEffectiveAccess] = useState<ShareAccessLevel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeVersion, setActiveVersion] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commentMode, setCommentMode] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(null);
  const [selectionRect, setSelectionRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [commentRefreshKey, setCommentRefreshKey] = useState(0);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() =>
    getResolvedTheme(getThemePreference()),
  );
  const [workspaceJoinPrompt, setWorkspaceJoinPrompt] = useState<{
    workspaceName: string;
    shareUrl: string;
  } | null>(null);
  const [openComments, setOpenComments] = useState<Comment[]>([]);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [scrollToCommentId, setScrollToCommentId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const highlightedCommentId = hoveredCommentId ?? selectedCommentId;

  useEffect(() => {
    if (!id) return;
    const artifactId = id;
    setLoading(true);
    setWorkspaceJoinPrompt(null);

    async function loadSharedArtifact() {
      const loadArtifact = async () => {
        const [artifactData, session] = await Promise.all([
          api.getPublicArtifact(artifactId),
          api.createPublicArtifactAccessSession(artifactId),
        ]);
        setArtifact({ ...artifactData.artifact, versions: artifactData.versions } as ArtifactDetail);
        setActiveVersion(artifactData.artifact.current_version);
        setEffectiveAccess(artifactData.effective_access);
        setAccessToken(session.token);
        setError(null);
      };

      try {
        await loadArtifact();
      } catch (err) {
        if (
          err instanceof ApiClientError &&
          err.status === 403 &&
          (err.data as { requires_workspace_membership?: boolean } | undefined)?.requires_workspace_membership
        ) {
          const workspaceName =
            (err.data as { workspace_name?: string } | undefined)?.workspace_name || 'Workspace';

          if (!getToken()) {
            setWorkspaceJoinPrompt({
              workspaceName,
              shareUrl: window.location.href,
            });
            setError(null);
            return;
          }

          const join = await api.joinWorkspaceForArtifact(artifactId);
          setWorkspaceId(join.workspace_id);
          await loadArtifact();
          return;
        }

        throw err;
      }
    }

    loadSharedArtifact()
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'This link is not currently available.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const latestVersion = artifact?.current_version ?? 1;
  const activeVersionId =
    artifact?.versions?.find((v) => v.version_number === activeVersion)?.id
      ?? artifact?.versions?.[0]?.id
      ?? null;
  const iframeSrc = artifact && accessToken && activeVersionId
    ? `${ARTIFACT_ORIGIN}/t/${accessToken}/${artifact.id}/${activeVersionId}/`
    : '';
  const contentType = (artifact as Record<string, unknown> | null)?.content_type as string || 'text/html';
  const isPdf = contentType === 'application/pdf';
  const isMarkdown = contentType.includes('markdown');
  const canComment = effectiveAccess === 'commenter' || effectiveAccess === 'editor';

  const shouldAutoCollapse =
    containerWidth > 0 &&
    containerWidth - SIDEBAR_WIDTH < MIN_IFRAME_WIDTH;
  const showDesktopSidebar = !shouldAutoCollapse && sidebarOpen;

  const postToIframe = useCallback((msg: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*');
  }, []);

  const { resolvedPositions, hasResolverResponded, iframeScroll } = useResolvedCommentAnchors({
    iframeLoaded,
    openComments,
    currentVersion: activeVersion,
    postToIframe,
  });

  const highlightCommentAnchor = useCallback(
    (comment: Comment, scroll: boolean) => {
      if (!comment.anchor) {
        postToIframe({ type: 'exhibit:clear-highlights' });
        return;
      }
      postToIframe({
        type: 'exhibit:highlight-anchor',
        payload: { anchor: comment.anchor, scroll },
      });
    },
    [postToIframe],
  );

  const restoreSelectedAnchorHighlight = useCallback(() => {
    const selectedComment = findCommentById(openComments, selectedCommentId);
    if (selectedComment) {
      highlightCommentAnchor(selectedComment, false);
      return;
    }
    postToIframe({ type: 'exhibit:clear-highlights' });
  }, [highlightCommentAnchor, openComments, postToIframe, selectedCommentId]);

  const handleLeaveAnchor = useCallback(() => {
    restoreSelectedAnchorHighlight();
  }, [restoreSelectedAnchorHighlight]);

  const handleMarkerHover = useCallback(
    (comment: Comment) => {
      highlightCommentAnchor(comment, false);
    },
    [highlightCommentAnchor],
  );

  const handleSidebarCommentHover = useCallback(
    (comment: Comment) => {
      setHoveredCommentId(comment.id);
      highlightCommentAnchor(comment, false);
    },
    [highlightCommentAnchor],
  );

  const handleSidebarCommentLeave = useCallback(
    (comment: Comment) => {
      setHoveredCommentId((current) => (current === comment.id ? null : current));
      restoreSelectedAnchorHighlight();
    },
    [restoreSelectedAnchorHighlight],
  );

  const handleSidebarCommentFocus = useCallback(
    (comment: Comment) => {
      setHoveredCommentId(null);
      setSelectedCommentId(comment.id);
      highlightCommentAnchor(comment, true);
    },
    [highlightCommentAnchor],
  );

  const handleMarkerClick = useCallback(
    (commentId: string) => {
      if (!sidebarOpen) setSidebarOpen(true);
      setSelectedCommentId(commentId);
      setHoveredCommentId(null);
      setScrollToCommentId(commentId);
      const comment = findCommentById(openComments, commentId);
      if (comment) highlightCommentAnchor(comment, true);
    },
    [highlightCommentAnchor, openComments, sidebarOpen],
  );

  useEffect(() => {
    return subscribeToThemePreference((preference) => {
      setResolvedTheme(getResolvedTheme(preference));
    });
  }, []);

  useEffect(() => {
    if (!iframeLoaded || !isMarkdown) return;
    postToIframe({ type: 'exhibit:set-theme', payload: { theme: resolvedTheme } });
  }, [iframeLoaded, isMarkdown, postToIframe, resolvedTheme]);

  const selectCursorTool = useCallback(() => {
    setPendingAnchor(null);
    setSelectionRect(null);
    setCommentMode(false);
  }, []);

  const selectCommentTool = useCallback(() => {
    if (isPdf || !canComment) return;
    setPendingAnchor(null);
    setSelectionRect(null);
    setCommentMode(true);
  }, [canComment, isPdf]);

  useEffect(() => {
    if ((isPdf || !canComment) && commentMode) setCommentMode(false);
  }, [canComment, commentMode, isPdf]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
      if (key === 'c' && !isPdf && canComment && !hasModifier) {
        event.preventDefault();
        selectCommentTool();
      } else if (key === 'v' && !hasModifier) {
        event.preventDefault();
        selectCursorTool();
      } else if (event.key === 'Escape' && commentMode) {
        event.preventDefault();
        selectCursorTool();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canComment, commentMode, isPdf, selectCommentTool, selectCursorTool]);

  useEffect(() => {
    if (!iframeLoaded || isPdf) return;
    postToIframe({
      type: 'exhibit:set-comment-mode',
      payload: { enabled: commentMode && canComment },
    });
  }, [canComment, commentMode, iframeLoaded, isPdf, postToIframe]);

  const handleCommentsChanged = useCallback(() => {
    setCommentRefreshKey((count) => count + 1);
    setPendingAnchor(null);
    setSelectionRect(null);
    setSelectedCommentId(null);
    setHoveredCommentId(null);
    setScrollToCommentId(null);
  }, []);

  useEffect(() => {
    if (!artifact?.id) return;
    let cancelled = false;

    api.listPublicComments(artifact.id, false)
      .then(({ comments }) => {
        if (!cancelled) setOpenComments(comments);
      })
      .catch(() => {
        if (!cancelled) setOpenComments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [artifact?.id, commentRefreshKey]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (ARTIFACT_ORIGIN && event.origin !== ARTIFACT_ORIGIN) return;
      const { type, payload } = event.data || {};
      if (type === 'artifact:loaded') {
        setIframeLoaded(true);
      } else if (type === 'exhibit:text-selected') {
        const { text, prefix, suffix, xpath, startOffset, endOffset, scrollY, rect } = payload;
        setPendingAnchor({
          type: 'text',
          text,
          prefix,
          suffix,
          xpath,
          startOffset,
          endOffset,
          scrollY: scrollY ?? 0,
          rect: rect ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height } : undefined,
        });
        setSelectionRect(rect);
      } else if (type === 'exhibit:element-selected') {
        if (!commentMode || !canComment || payload?.anchor?.type !== 'element' || !payload?.anchor?.xpath || !payload?.rect) return;
        const { anchor, rect } = payload;
        setPendingAnchor({
          type: 'element',
          xpath: anchor.xpath,
          label: anchor.label,
          tagName: anchor.tagName,
          text: anchor.text,
          fingerprint: anchor.fingerprint,
          rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
        });
        setSelectionRect(rect);
      } else if (type === 'exhibit:selection-cleared') {
        setPendingAnchor(null);
        setSelectionRect(null);
      } else if (type === 'exhibit:marker-clicked') {
        if (payload?.commentId) handleMarkerClick(payload.commentId);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [canComment, commentMode, handleMarkerClick]);

  const handleInlineCommentSubmit = useCallback(
    async (body: string) => {
      if (!artifact || !pendingAnchor) return;
      const guest = getGuestIdentity();
      await api.createPublicComment(artifact.id, {
        body,
        version_number: activeVersion,
        anchor: pendingAnchor,
        guest_alias: guest.alias,
        guest_session_id: guest.sessionId,
      });
      setPendingAnchor(null);
      setSelectionRect(null);
      handleCommentsChanged();
      if (!sidebarOpen) setSidebarOpen(true);
    },
    [artifact, activeVersion, pendingAnchor, sidebarOpen, handleCommentsChanged],
  );

  const handleSelectionCancel = useCallback(() => {
    setPendingAnchor(null);
    setSelectionRect(null);
  }, []);

  const handleHighlightHandled = useCallback(() => {
    setScrollToCommentId(null);
  }, []);

  const handleOpenCommentsChange = useCallback((comments: Comment[]) => {
    setOpenComments(comments);
  }, []);

  const composerPosition = selectionRect
    ? {
        x: selectionRect.x + selectionRect.width / 2,
        y: selectionRect.y + selectionRect.height,
      }
    : { x: 0, y: 0 };

  if (loading) {
    return <div className="h-screen flex items-center justify-center">Loading...</div>;
  }

  if (error || !artifact) {
    if (workspaceJoinPrompt) {
      return (
        <div className="min-h-screen flex items-center justify-center p-[var(--sp-6)]">
          <div className="max-w-[420px] text-center">
            <p className="text-[22px] font-semibold m-0" style={{ color: 'var(--color-text-neutral-strong)' }}>
              Join {workspaceJoinPrompt.workspaceName}
            </p>
            <p className="text-[14px] mt-[var(--sp-3)]" style={{ color: 'var(--color-text-neutral-subtle)' }}>
              This artifact is shared with workspace members. Sign in and you&apos;ll be added to the workspace before the artifact opens.
            </p>
            <Link
              to={`/login?redirect=${encodeURIComponent(workspaceJoinPrompt.shareUrl)}`}
              className="inline-flex mt-[var(--sp-4)] h-[40px] items-center rounded-[var(--radius-md)] px-[var(--sp-4)] font-semibold no-underline"
              style={{
                background: 'var(--color-surface-action-strong-default)',
                color: '#fff',
              }}
            >
              Continue
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-[var(--sp-6)]">
        <div className="max-w-[420px] text-center">
          <p className="text-[22px] font-semibold m-0" style={{ color: 'var(--color-text-neutral-strong)' }}>
            This link is not open right now
          </p>
          <p className="text-[14px] mt-[var(--sp-3)]" style={{ color: 'var(--color-text-neutral-subtle)' }}>
            {error || 'Sign in with a workspace account that has access, or ask the owner to switch this artifact to Public.'}
          </p>
          <Link
            to="/login"
            className="inline-flex mt-[var(--sp-4)] h-[40px] items-center rounded-[var(--radius-md)] px-[var(--sp-4)] font-semibold no-underline"
            style={{
              background: 'var(--color-surface-action-strong-default)',
              color: '#fff',
            }}
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen" ref={containerRef}>
      <div
        className="flex items-center h-[56px] min-h-[56px] px-[var(--sp-4)] gap-[var(--sp-3)] border-b"
        style={{
          background: 'var(--color-surface-neutral-default)',
          borderColor: 'var(--color-border-neutral-subtle-default)',
        }}
      >
        <button
          onClick={() => navigate('/')}
          className="w-[32px] h-[32px] rounded-[var(--radius-md)] border-0 bg-transparent cursor-pointer"
          style={{ color: 'var(--color-icon-neutral-medium)' }}
          title="Back"
        >
          <i className="icon icon-app-chevron-left" />
        </button>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold truncate" style={{ color: 'var(--color-text-neutral-strong)' }}>
            {artifact.title}
          </div>
          <div className="text-[13px]" style={{ color: 'var(--color-text-neutral-subtle)' }}>
            Shared artifact
          </div>
        </div>
        <span
          className="inline-flex items-center h-[20px] px-[var(--sp-2)] rounded-[var(--radius-full)] text-[13px] font-semibold"
          style={{
            background: 'var(--color-surface-version-badge)',
            border: '1px solid var(--color-border-version-badge)',
            color: 'var(--color-text-neutral-subtle)',
          }}
        >
          v{activeVersion}
        </span>
        <div className="ml-auto flex items-center gap-[var(--sp-2)]">
          <button
            type="button"
            onClick={() => setSidebarOpen((current) => !current)}
            className="w-[36px] h-[36px] flex items-center justify-center rounded-[var(--radius-md)] cursor-pointer border transition-colors duration-[120ms]"
            style={{
              background: sidebarOpen
                ? 'var(--color-surface-action-medium-default)'
                : 'var(--color-surface-neutral-default)',
              borderColor: sidebarOpen
                ? 'var(--color-border-action-strong)'
                : 'var(--color-border-neutral-subtle-default)',
              color: sidebarOpen
                ? 'var(--color-text-action-strong-default)'
                : 'var(--color-text-neutral-subtle)',
            }}
            title="Comments"
            aria-label="Comments"
          >
            <i className="icon icon-app-feedback" style={{ fontSize: 16 }} />
          </button>
          <ArtifactActionsMenu
            artifact={artifact}
            icon="burger"
            buttonTitle="Artifact options"
            resolveDownloadHref={async (currentArtifact) => {
              if (!currentArtifact.latest_version_id) return null;
              const session = await api.createPublicArtifactAccessSession(currentArtifact.id);
              return `${ARTIFACT_ORIGIN}/download/${session.token}/${currentArtifact.id}/${currentArtifact.latest_version_id}`;
            }}
          />
        </div>
      </div>

      {!canComment && (
        <InfoBanner message="This shared link is view-only." />
      )}

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
        <div className="flex-1 min-w-0 relative">
          {!iframeLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              Loading artifact...
            </div>
          )}
          {isPdf ? (
            <embed
              src={iframeSrc}
              type="application/pdf"
              title={artifact.title}
              className="w-full h-full border-0"
              onLoad={() => setIframeLoaded(true)}
            />
          ) : (
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title={artifact.title}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              onLoad={() => {
                setIframeLoaded(true);
                if (isMarkdown) {
                  postToIframe({ type: 'exhibit:set-theme', payload: { theme: resolvedTheme } });
                }
              }}
            />
          )}

          {!isPdf && iframeLoaded && (
            <CommentMarkers
              comments={openComments}
              onMarkerClick={handleMarkerClick}
              onMarkerHover={handleMarkerHover}
              onMarkerLeave={handleLeaveAnchor}
              resolvedPositions={resolvedPositions}
              hasResolverResponded={hasResolverResponded}
              iframeScrollY={iframeScroll.scrollY}
              iframeViewportHeight={iframeScroll.viewportHeight}
              highlightedCommentId={highlightedCommentId}
            />
          )}

          {commentMode && canComment && !isPdf && iframeLoaded && (
            <div
              className="absolute top-[14px] left-1/2 z-40 flex -translate-x-1/2 items-center gap-[6px] rounded-full px-[12px] py-[6px] text-[13px] font-semibold pointer-events-none"
              style={{
                background: 'var(--color-surface-neutral-default)',
                border: '1px solid var(--color-border-neutral-subtle-default)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                color: 'var(--color-text-neutral-strong)',
              }}
            >
              <span className="h-[6px] w-[6px] rounded-full" style={{ background: '#2563EB' }} />
              Click a visible element to comment
              <span
                className="ml-[6px] rounded-[4px] px-[5px] py-px text-[11px] font-medium"
                style={{ background: 'var(--rd-surface-highlight)', color: 'var(--rd-text-meta)' }}
              >
                Esc
              </span>
            </div>
          )}

          {!isPdf && canComment && iframeLoaded && (
            <CommentModeToolbar
              commentMode={commentMode}
              onSelectCursor={selectCursorTool}
              onSelectComment={selectCommentTool}
            />
          )}

          {/* Inline selection composer */}
          {selectionRect && pendingAnchor && !isPdf && canComment && (
            <SelectionComposer
              visible={!!selectionRect}
              position={composerPosition}
              selectedText={
                pendingAnchor.type === 'text'
                  ? pendingAnchor.text
                  : pendingAnchor.type === 'element'
                    ? pendingAnchor.label ?? 'Selected element'
                    : ''
              }
              onSubmit={handleInlineCommentSubmit}
              onCancel={handleSelectionCancel}
            />
          )}
        </div>

        <div className="hidden md:flex">
          {showDesktopSidebar && (
            <CommentSidebar
              artifactId={artifact.id}
              currentUserId={`guest:${getGuestIdentity().sessionId}`}
              currentVersion={activeVersion}
              collapsed={false}
              onToggle={() => setSidebarOpen(false)}
              onCommentsChanged={handleCommentsChanged}
              highlightedCommentId={highlightedCommentId}
              scrollToCommentId={scrollToCommentId}
              onHighlightHandled={handleHighlightHandled}
              onCommentFocus={handleSidebarCommentFocus}
              onCommentHover={handleSidebarCommentHover}
              onCommentLeave={handleSidebarCommentLeave}
              onOpenCommentsChange={handleOpenCommentsChange}
              refreshNonce={commentRefreshKey}
              listComments={(artifactId, resolved) => api.listPublicComments(artifactId, resolved)}
              createComment={({ artifactId, body, versionNumber, parentId, anchor }) => {
                const guest = getGuestIdentity();
                return api.createPublicComment(artifactId, {
                  body,
                  version_number: versionNumber,
                  parent_id: parentId,
                  anchor,
                  guest_alias: guest.alias,
                  guest_session_id: guest.sessionId,
                });
              }}
              allowMentions={false}
              allowResolve={false}
              allowReactions={false}
              enableRealtime={false}
              canComment={canComment}
            />
          )}
        </div>

        {sidebarOpen && (
          <div
            className={`${shouldAutoCollapse ? '' : 'md:hidden'} border-t`}
            style={{ borderColor: 'var(--color-border-neutral-subtle-default)' }}
          >
            <CommentSidebar
              artifactId={artifact.id}
              currentUserId={`guest:${getGuestIdentity().sessionId}`}
              currentVersion={activeVersion}
              collapsed={false}
              onToggle={() => setSidebarOpen(false)}
              onCommentsChanged={handleCommentsChanged}
              highlightedCommentId={highlightedCommentId}
              scrollToCommentId={scrollToCommentId}
              onHighlightHandled={handleHighlightHandled}
              onCommentFocus={handleSidebarCommentFocus}
              onCommentHover={handleSidebarCommentHover}
              onCommentLeave={handleSidebarCommentLeave}
              onOpenCommentsChange={handleOpenCommentsChange}
              refreshNonce={commentRefreshKey}
              listComments={(artifactId, resolved) => api.listPublicComments(artifactId, resolved)}
              createComment={({ artifactId, body, versionNumber, parentId, anchor }) => {
                const guest = getGuestIdentity();
                return api.createPublicComment(artifactId, {
                  body,
                  version_number: versionNumber,
                  parent_id: parentId,
                  anchor,
                  guest_alias: guest.alias,
                  guest_session_id: guest.sessionId,
                });
              }}
              allowMentions={false}
              allowResolve={false}
              allowReactions={false}
              enableRealtime={false}
              canComment={canComment}
            />
          </div>
        )}
      </div>

      {artifact.versions && artifact.versions.length > 0 && (
        <VersionBar
          versions={artifact.versions}
          activeVersion={activeVersion}
          latestVersion={latestVersion}
          onVersionChange={setActiveVersion}
        />
      )}
    </div>
  );
}
